/**
 * FLAGS registry — string-id source of truth for scripted-handler flag
 * bits set via `runState.setFlag(FLAGS.X)`.
 *
 * Decision 18 (eng review): plain `as const` object, NOT a TypeScript
 * enum (enums add runtime cost + the inverse-lookup baggage), NOT free-
 * form strings (typo-prone). Handlers import { FLAGS } and reference
 * `FLAGS.OAK_INTRO_DONE`; typos surface as compile errors.
 *
 * Adding a flag: add the key here + populate ScriptCtx.FLAGS via
 * scriptRunner. The rebuildNpcSprites filter consumes `FLAG_HIDE_*`
 * names via `runState.hasFlag(npc.flag)` for cartridge-faithful NPC
 * visibility (PR 12 / H9).
 */

// ─── Scripted state flags ────────────────────────────────────────────

export const FLAGS = {
  /** Oak intercept on Route 1 has played; he no longer blocks the player. */
  OAK_INTRO_DONE: 'FLAG_OAK_INTRO_DONE',
  /** Player has picked a starter ball in Oak's Lab. Replaces the empty-party-
   *  fires-modal hack (CLAUDE.md gotcha M3). Reset Run clears this, so a
   *  post-reset lab entry re-fires the starter modal. */
  STARTER_PICKED: 'FLAG_STARTER_PICKED',
  /** Rival has been defeated in the Pallet lab cinematic. Suppresses re-fire
   *  of the rival walk-in beat. */
  RIVAL_DEFEATED_PALLET: 'FLAG_RIVAL_DEFEATED_PALLET',
  /** PR A3: single-fire latch for the labPostPick cinematic. Set at
   *  handler ENTRY (not end) so a WAKE during the cinematic (e.g.,
   *  PartySheet/Settings modal close mid-walk) doesn't re-dispatch.
   *  Per eng-review decision 3. */
  LAB_POST_PICK_DONE: 'FLAG_LAB_POST_PICK_DONE',
  /** PR A4: set by BattleScene's eventHandlers on rival p1-victory.
   *  Signals to the WAKE handler that the post-battle cinematic should
   *  fire ("Smell you later!" + walk out south door). Cleared at
   *  rival post-battle handler-end; the latch is RIVAL_POST_BATTLE_DONE
   *  below. */
  RIVAL_BATTLE_RESOLVED: 'FLAG_RIVAL_BATTLE_RESOLVED',
  /** PR A4: single-fire latch for the rival post-battle cinematic.
   *  Same pattern as LAB_POST_PICK_DONE — set at handler ENTRY so
   *  re-WAKEs during the cinematic gate-deny re-dispatch. */
  RIVAL_POST_BATTLE_DONE: 'FLAG_RIVAL_POST_BATTLE_DONE',

  // ─── FLAG_HIDE_* — NPC visibility (PR 12 / H9) ─────────────────────
  //
  // The `flag` field on each NPC in runtime.json names a cartridge bit
  // that controls visibility. rebuildNpcSprites filters out an NPC when
  // `runState.hasFlag(npc.flag)` returns true.
  //
  // Cartridge defaults vary per flag. `INITIAL_HIDDEN_FLAGS` below
  // enumerates the ones that ship default-SET so reset() (fresh game +
  // Reset Run) seeds them at the start of every run.

  /** Oak NPC in Pallet Town exterior — default-SET (Oak not in Pallet
   *  pre-intercept). Stays set through MVP — Oak intercept brings him
   *  TO the lab, not back to Pallet exterior. */
  HIDE_OAK_IN_PALLET_TOWN: 'FLAG_HIDE_OAK_IN_PALLET_TOWN',
  /** Oak NPC inside his lab — default-SET (Oak isn't in the lab until
   *  the intercept walks the player in). PR 13 (H6) clears this at
   *  intercept-completion. */
  HIDE_OAK_IN_HIS_LAB: 'FLAG_HIDE_OAK_IN_HIS_LAB',
  /** Rival NPC inside Oak's lab — default-SET (rival cinematic in PR 15
   *  reveals him post-starter-pick + walks him to battle position). */
  HIDE_RIVAL_IN_LAB: 'FLAG_HIDE_RIVAL_IN_LAB',
  /** Pokédex prop in Oak's lab — default-SET (cartridge: Oak hands a
   *  Pokédex after the player returns with a starter). v0.1 keeps it
   *  hidden permanently (Pokédex modal is its own UI surface). */
  HIDE_POKEDEX: 'FLAG_HIDE_POKEDEX',

  /** The 3 starter balls on Oak's lab table — default-SET (hidden until
   *  the Oak intercept walks the player into the lab). Cartridge-faithful
   *  empty-lab posture: if the player wanders into the lab before
   *  triggering the intercept cinematic, Oak isn't there and no balls
   *  are on the pedestals. The Oak intercept handler clears these flags
   *  alongside HIDE_OAK_IN_HIS_LAB at cinematic completion; DraftScene.
   *  commit re-sets them when the player picks any starter so the table
   *  ends empty post-pick (cartridge: Oak collects the unpicked balls). */
  HIDE_BULBASAUR_BALL: 'FLAG_HIDE_BULBASAUR_BALL',
  HIDE_SQUIRTLE_BALL: 'FLAG_HIDE_SQUIRTLE_BALL',
  HIDE_CHARMANDER_BALL: 'FLAG_HIDE_CHARMANDER_BALL',

  /** Town Map object in Rival's House — default-SET, permanently: DAISY
   *  grants the TOWN MAP item directly on first talk (G9 handler); the
   *  world object never appears. */
  HIDE_TOWN_MAP: 'FLAG_HIDE_TOWN_MAP',

  /** Pewter City NPCs that spawn after specific cartridge events.
   *  All default-SET; v0.1 has no triggering events. */
  HIDE_PEWTER_MUSEUM_GUIDE: 'FLAG_HIDE_PEWTER_MUSEUM_GUIDE',
  HIDE_PEWTER_CITY_GYM_GUIDE: 'FLAG_HIDE_PEWTER_CITY_GYM_GUIDE',
  HIDE_PEWTER_CITY_RUNNING_SHOES_GUY: 'FLAG_HIDE_PEWTER_CITY_RUNNING_SHOES_GUY',

  /** Key-item use bits (G9). Set the first time the player uses the
   *  item from the bag; never cleared within a run. Not visibility
   *  flags — nothing hides on them; they exist so a future surface
   *  (dialogue conditions, feats) can ask "has the player used X". */
  USED_TOWN_MAP: 'FLAG_USED_TOWN_MAP',
  USED_OLD_ROD: 'FLAG_USED_OLD_ROD',

  /** Route 2 + Viridian Forest + Viridian City item balls. Cartridge
   *  default-UNSET (balls visible from run start); the G3 pickup
   *  handler SETs the flag when the player collects the ball. NOT in
   *  INITIAL_HIDDEN_FLAGS — pre-G3 they were default-SET "to keep the
   *  world tidy"; that posture ended when the bag landed. */
  HIDE_ROUTE2_ETHER: 'FLAG_HIDE_ROUTE2_ETHER',
  HIDE_ROUTE2_PARALYZE_HEAL: 'FLAG_HIDE_ROUTE2_PARALYZE_HEAL',
  HIDE_VIRIDIAN_CITY_POTION: 'FLAG_HIDE_VIRIDIAN_CITY_POTION',
  HIDE_VIRIDIAN_FOREST_POKE_BALL: 'FLAG_HIDE_VIRIDIAN_FOREST_POKE_BALL',
  HIDE_VIRIDIAN_FOREST_ANTIDOTE: 'FLAG_HIDE_VIRIDIAN_FOREST_ANTIDOTE',
  HIDE_VIRIDIAN_FOREST_POTION: 'FLAG_HIDE_VIRIDIAN_FOREST_POTION',
  HIDE_VIRIDIAN_FOREST_POTION_2: 'FLAG_HIDE_VIRIDIAN_FOREST_POTION_2',
} as const;

export type FlagKey = keyof typeof FLAGS;
export type FlagValue = (typeof FLAGS)[FlagKey];

/** Cartridge-default-SET FLAG_HIDE_* flags. `runState.reset()` seeds these
 *  so fresh game + Reset Run both apply the right initial visibility. Per-
 *  flag rationale lives in the FLAGS entries above. The 3 starter ball
 *  flags are included so the lab reads as empty if the player wanders in
 *  pre-cinematic; the Oak intercept handler clears them at completion. */
export const INITIAL_HIDDEN_FLAGS: readonly FlagValue[] = [
  FLAGS.HIDE_OAK_IN_PALLET_TOWN,
  FLAGS.HIDE_OAK_IN_HIS_LAB,
  FLAGS.HIDE_RIVAL_IN_LAB,
  FLAGS.HIDE_POKEDEX,
  FLAGS.HIDE_BULBASAUR_BALL,
  FLAGS.HIDE_SQUIRTLE_BALL,
  FLAGS.HIDE_CHARMANDER_BALL,
  FLAGS.HIDE_TOWN_MAP,
  FLAGS.HIDE_PEWTER_MUSEUM_GUIDE,
  FLAGS.HIDE_PEWTER_CITY_GYM_GUIDE,
  FLAGS.HIDE_PEWTER_CITY_RUNNING_SHOES_GUY,
  // Item-ball hide flags are deliberately absent: balls are visible
  // from run start (cartridge default); pickup SETs them (G3).
] as const;
